import React from "react";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import moment from "moment";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import Table from "examples/Tables/Table";
import GoalActionButton from "layouts/goals/components/GoalActionButton";
import GoalPill from "layouts/goals/components/GoalPill";
import { PUBLIC_HOLIDAYS_COLUMNS } from "./publicHolidays.constants";

const getStatus = (date) => {
  const day = moment(date);
  if (day.isSame(moment(), "day")) return { label: "Today", color: "warning" };
  if (day.isAfter(moment(), "day")) return { label: "Upcoming", color: "info" };
  return { label: "Past", color: "secondary" };
};

function PublicHolidaysTable({ holidays, onEdit, onArchive, isLoading, emptyMessage }) {
  const rows = holidays.map((holiday) => {
    const status = getStatus(holiday.date);
    const isPast = status.label === "Past";
    return {
      holiday: (
        <SuiBox display="flex" alignItems="center" gap={1.5} sx={{ opacity: isPast ? 0.7 : 1 }}>
          <SuiBox
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0b7c99",
              backgroundColor: "#e0f5fb",
            }}
          >
            <Icon sx={{ fontSize: "20px !important" }}>celebration</Icon>
          </SuiBox>
          <SuiTypography variant="button" fontWeight="medium" color="dark">
            {holiday.name}
          </SuiTypography>
        </SuiBox>
      ),
      date: (
        <SuiTypography variant="caption" color="secondary" fontWeight="medium">
          {moment(holiday.date).format("DD MMM YYYY")}
        </SuiTypography>
      ),
      day: (
        <SuiTypography variant="caption" color="secondary">
          {moment(holiday.date).format("dddd")}
        </SuiTypography>
      ),
      status: <GoalPill color={status.color}>{status.label}</GoalPill>,
      actions: (
        <SuiBox display="flex" gap={1} alignItems="center" justifyContent="center">
          <GoalActionButton
            icon="edit"
            tooltip="Edit holiday"
            color="info"
            onClick={() => onEdit(holiday)}
          />
          <GoalActionButton
            icon="archive"
            tooltip="Archive holiday"
            color="error"
            onClick={() => onArchive(holiday)}
          />
        </SuiBox>
      ),
    };
  });

  return (
    <Card>
      <SuiBox p={3}>
        {isLoading ? (
          <SuiBox display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress color="dark" size={30} />
          </SuiBox>
        ) : !isEmpty(rows) ? (
          <Table columns={PUBLIC_HOLIDAYS_COLUMNS} rows={rows} />
        ) : (
          <SuiBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <Icon fontSize="large" color="secondary" sx={{ opacity: 0.5 }}>
              event
            </Icon>
            <SuiTypography variant="button" color="text" fontWeight="regular" mt={1}>
              {emptyMessage}
            </SuiTypography>
          </SuiBox>
        )}
      </SuiBox>
    </Card>
  );
}

PublicHolidaysTable.propTypes = {
  holidays: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEdit: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  emptyMessage: PropTypes.string.isRequired,
};

export default PublicHolidaysTable;
